import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import ViewBilan from '@/components/demo-organizer/ViewBilan';
import ViewCreer from '@/components/demo-organizer/ViewCreer';
import ViewInscriptions from '@/components/demo-organizer/ViewInscriptions';
import ViewResultats from '@/components/demo-organizer/ViewResultats';
import { ROUTES } from '@/config/routes.ts';
import { ArrowRight, BarChart2, CreditCard, Pause, Play, Trophy, Zap } from 'lucide-react';

// ─── Types & constantes ───────────────────────────────────────────────────────
const TABS = ['creer', 'inscriptions', 'resultats', 'bilan'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  creer: '📝 Créer',
  inscriptions: '💳 Inscriptions',
  resultats: '📊 Résultats',
  bilan: '📈 Bilan',
};

const TAB_SUBTITLES: Record<Tab, string> = {
  creer: 'Créer un concours en 3 minutes · Règles · Dotation · Terrains',
  inscriptions: 'Ouvrir les inscriptions · Paiement Stripe · Listes & quotas',
  resultats: 'Saisie classement · Podium public · Export CSV',
  bilan: 'Résultats · Exports · Recettes · Compta',
};

// ─── Page principale ──────────────────────────────────────────────────────────
const BENEFITS = [
  {
    icon: Zap,
    title: 'Concours en 3 minutes',
    desc: 'Création guidée, règles et quotas. Page concours prête à partager.',
    gradient: 'from-blue-500/10 to-blue-600/5',
    border: 'border-blue-500/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-500/10',
  },
  {
    icon: CreditCard,
    title: 'Paiements en ligne',
    desc: 'Stripe Connect, reçus et listes automatiques. Moins d’erreurs, plus de traçabilité.',
    gradient: 'from-emerald-500/10 to-emerald-600/5',
    border: 'border-emerald-500/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-500/10',
  },
  {
    icon: Trophy,
    title: 'Podium en un clic',
    desc: 'Saisissez le classement, publiez le podium instantanément. Ex-æquos et rangs spéciaux supportés.',
    gradient: 'from-amber-500/10 to-amber-600/5',
    border: 'border-amber-500/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-500/10',
  },
  {
    icon: BarChart2,
    title: 'Bilan & exports',
    desc: 'Résultats, participants, paiements, rapport concours. Prêt pour la compta.',
    gradient: 'from-purple-500/10 to-purple-600/5',
    border: 'border-purple-500/20',
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-500/10',
  },
] as const;

export default function DemoOrganizer() {
  const [activeTab, setActiveTab] = useState<Tab>('creer');
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const isPausedRef = useRef(false);
  const activeTabRef = useRef<Tab>('creer');
  const progressRef = useRef(0);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    activeTabRef.current = tab;
    progressRef.current = 0;
    setProgress(0);
  };

  const togglePause = () => {
    const next = !isPausedRef.current;
    isPausedRef.current = next;
    setIsPaused(next);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPausedRef.current) return;

      progressRef.current += 0.5;
      setProgress(progressRef.current);

      if (progressRef.current >= 100) {
        progressRef.current = 0;
        const idx = TABS.findIndex(t => t === activeTabRef.current);
        const nextTab = TABS[(idx + 1) % TABS.length];
        activeTabRef.current = nextTab;
        setActiveTab(nextTab);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <PageMeta
        title='Démo Organisateur'
        description="Découvrez l'expérience BoulesConnect pour les organisateurs : création de concours, inscriptions & paiements Stripe, saisie des résultats et bilan avec exports."
        robots='noindex, nofollow'
      />

      <div className='min-h-screen bg-white dark:bg-[#030712] text-slate-900 dark:text-white'>
        {/* ─── Hero ─── */}
        <section className='pt-32 pb-14 px-6 text-center'>
          <div className='max-w-4xl mx-auto'>
            {/* Role selector */}
            <div className='flex items-center justify-center flex-wrap gap-3 mb-12'>
              <Link
                to={'/demo'}
                className='border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600 px-5 py-2 rounded-full font-bold text-sm'
              >
                🧑‍🤝‍🧑 Joueur
              </Link>

              <Link
                to={'/demo-club'}
                className='border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-600 px-5 py-2 rounded-full font-bold text-sm'
              >
                🏟️ Club
              </Link>

              <button className='bg-linear-to-br from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg shadow-blue-600/30 dark:shadow-blue-900/40'>
                🏆 Organisateur
                <span className='bg-white/20 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider'>
                  Live
                </span>
              </button>
            </div>

            <p className='text-blue-600 dark:text-blue-400 font-bold tracking-[0.2em] uppercase text-xs mb-5'>
              Démo interactive · Rôle Organisateur
            </p>

            <h1 className='text-4xl md:text-6xl font-black tracking-tight leading-[1.05] mb-6 text-slate-900 dark:text-blue-400'>
              Organiser un concours,
              <br />
              <span className='text-transparent bg-clip-text bg-linear-to-r from-blue-500 via-indigo-500 to-emerald-500 dark:from-blue-400 dark:via-indigo-400 dark:to-emerald-400'>
                sans friction.
              </span>
            </h1>

            <p className='text-slate-500 dark:text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed'>
              Création, inscriptions, paiements Stripe Connect, saisie des résultats et exports : tout dans une seule
              plateforme.
            </p>
          </div>
        </section>

        {/* ─── Demo simulator ─── */}
        <section className='px-4 md:px-10 pb-24'>
          <div className='max-w-7xl mx-auto'>
            <div className='relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl shadow-slate-200/60 dark:border-slate-800 dark:bg-[#0B1120] dark:shadow-black/60'>
              {/* Tab nav */}
              <div className='flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/70 px-5 py-4 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/70'>
                <div className='min-w-0'>
                  <p className='text-[9px] font-black uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400'>
                    Parcours organisateur
                  </p>
                  <p className='mt-0.5 truncate text-sm font-bold text-slate-600 dark:text-white'>
                    {TAB_SUBTITLES[activeTab]}
                  </p>
                </div>

                <div className='flex shrink-0 gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white/80 p-1.5 dark:border-slate-700 dark:bg-slate-800/80'>
                  {TABS.map((tab, i) => (
                    <button
                      key={tab}
                      onClick={() => switchTab(tab)}
                      className={[
                        'flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2 text-xs font-bold transition-all',
                        activeTab === tab
                          ? 'bg-blue-600 text-white shadow-[0_4px_14px_rgba(59,130,246,0.35)]'
                          : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'text-[10px] font-black',
                          activeTab === tab ? 'text-blue-200' : 'text-slate-400 dark:text-slate-600',
                        ].join(' ')}
                      >
                        {i + 1}
                      </span>
                      {TAB_LABELS[tab]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div className='min-h-125 p-6 md:p-10 text-slate-900 dark:text-slate-50'>
                {activeTab === 'creer' && <ViewCreer key='creer' />}
                {activeTab === 'inscriptions' && <ViewInscriptions key='inscriptions' />}
                {activeTab === 'resultats' && <ViewResultats key='resultats' />}
                {activeTab === 'bilan' && <ViewBilan key='bilan' />}
              </div>

              {/* Playback bar */}
              <div className='flex items-center gap-5 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/80'>
                <button
                  onClick={togglePause}
                  aria-label={isPaused ? 'Reprendre' : 'Pause'}
                  className={[
                    'rounded-full border p-2 transition hover:scale-110',
                    'border-slate-200 bg-white text-slate-600 hover:text-blue-600',
                    'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-blue-400',
                    isPaused ? 'ring-2 ring-blue-500/20' : '',
                  ].join(' ')}
                >
                  {isPaused ? (
                    <Play className='h-4 w-4 fill-current ml-0.5' />
                  ) : (
                    <Pause className='h-4 w-4 fill-current' />
                  )}
                </button>

                <div className='h-1 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800'>
                  <div
                    className={[
                      'h-full rounded-full bg-linear-to-r from-blue-500 via-indigo-500 to-emerald-400 transition-all duration-100 ease-linear',
                      isPaused ? 'opacity-30' : '',
                    ].join(' ')}
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className='flex items-center gap-2'>
                  {TABS.map(tab => (
                    <button
                      key={tab}
                      onClick={() => switchTab(tab)}
                      aria-label={TAB_LABELS[tab]}
                      className={[
                        'rounded-full transition-all',
                        activeTab === tab
                          ? 'h-1.5 w-4 bg-blue-500 dark:bg-blue-400'
                          : 'h-1.5 w-1.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-500',
                      ].join(' ')}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Benefits ─── */}
        <section className='px-6 py-24 bg-slate-50 dark:bg-[#040B18]'>
          <div className='max-w-6xl mx-auto'>
            <div className='text-center mb-16'>
              <p className='text-blue-600 dark:text-blue-400 font-bold text-xs uppercase tracking-[0.2em] mb-5'>
                Pourquoi BoulesConnect (Organisateur)
              </p>
              <h2 className='text-3xl md:text-4xl font-black text-slate-900 dark:text-white leading-snug'>
                Plus d’inscrits,
                <br />
                <span className='text-transparent bg-clip-text bg-linear-to-r from-blue-500 to-emerald-500 dark:from-blue-400 dark:to-emerald-400'>
                  moins de charge mentale.
                </span>
              </h2>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-5'>
              {BENEFITS.map(({ icon: Icon, title, desc, gradient, border, iconColor, iconBg }) => (
                <div
                  key={title}
                  className={`bg-linear-to-br ${gradient} ${border} border rounded-3xl p-7 hover:-translate-y-1 transition-all`}
                >
                  <div className={`w-11 h-11 ${iconBg} rounded-2xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <h3 className='font-black text-slate-900 dark:text-white text-lg mb-3 leading-snug'>{title}</h3>
                  <p className='text-slate-500 text-sm leading-relaxed'>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CTA final ─── */}
        <section className='px-6 py-28 text-center'>
          <div className='max-w-xl mx-auto'>
            <p className='text-slate-500 dark:text-slate-600 text-sm uppercase tracking-widest font-bold mb-5'>
              Commission Partenaire 2 % + 0,30 € (min 0,75 €) · 0 € fixe
            </p>
            <h2 className='text-3xl md:text-4xl font-black text-slate-900 dark:text-white mb-6 leading-snug'>
              Prêt à organiser ?
            </h2>
            <p className='text-slate-500 text-lg mb-10'>Mise en ligne rapide. Paiements Stripe. Exports inclus.</p>

            <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link
                to={ROUTES.REGISTER}
                className='w-full sm:w-auto bg-linear-to-r from-blue-600 to-indigo-600 text-white px-10 py-4 rounded-2xl font-black text-base flex items-center justify-center gap-2 hover:shadow-2xl hover:shadow-blue-600/40 dark:hover:shadow-blue-900/50 hover:-translate-y-0.5 transition-all'
              >
                Créer mon concours <ArrowRight className='w-5 h-5' />
              </Link>

              <Link
                to={ROUTES.TARIFS}
                className='w-full sm:w-auto bg-slate-100 border border-slate-200 text-slate-700 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 px-10 py-4 rounded-2xl font-bold text-base hover:bg-slate-200 dark:hover:bg-slate-800 transition-all'
              >
                Voir les tarifs
              </Link>
            </div>

            <p className='mt-8 text-slate-500 dark:text-slate-700 text-sm'>
              Commission Partenaire 2 % + 0,30 € (min 0,75 €) par inscription payée · 0 € fixe · Concours illimités
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
